/**
 * Багфикс-агент: запросы админов к облачному агенту Cursor, канбан по статусам.
 * Общие типы клиента и сервера.
 */

export const TASK_STATUSES = ["request", "in_progress", "needs_attention", "rework", "review", "awaiting_merge", "merged", "paused"] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

export const STATUS_LABELS: Record<TaskStatus, string> = {
  request: "Запрос",
  in_progress: "В работе",
  needs_attention: "Требует реакции",
  rework: "Возвращено на доработку",
  review: "На проверке",
  awaiting_merge: "Ожидает слияния",
  merged: "Слито",
  paused: "На паузе",
}

/** Статусы, в которых агент работает (есть активный или ожидающий запуска run) */
export const WORKING_STATUSES: readonly TaskStatus[] = ["in_progress", "rework"]

export const TASK_ID_RE = /^bf_[a-f0-9]{16}$/
export const ATTACHMENT_ID_RE = /^ba_[a-f0-9]{20}$/

export const MAX_ATTACHMENTS = 20

export interface Person {
  id: number
  name: string
}

export type AttachmentKind = "image" | "video" | "audio" | "file"

export interface Attachment {
  id: string
  name: string
  mime: string
  size: number
  kind: AttachmentKind
  /** Путь раздачи файла: /api/bugfix-agent/files/{id}/{name} (без авторизации, id случайный) */
  url: string
  createdAt: string
  uploadedBy: Person | null
  /** Файл получен от агента (скриншот отчёта, артефакт) */
  fromAgent?: boolean
  /** Ассет API v3 с содержимым; без него — прежний файл на диске или в GridFS */
  assetId?: number
}

export type Actor = { kind: "human"; id: number; name: string } | { kind: "agent" } | { kind: "system" }

export type EventType =
  | "created"
  | "status"
  | "message"
  | "question"
  | "progress"
  | "report"
  | "comment"
  | "run_started"
  | "run_finished"
  | "run_error"
  | "stopped"
  | "error"

export interface TaskEvent {
  id: string
  at: string
  type: EventType
  actor: Actor
  text?: string
  from?: TaskStatus
  to?: TaskStatus
  attachments?: Attachment[]
  runId?: string
}

/** Типы событий, которые показываются в чате с агентом (остальные — только в ленте активности) */
export const CHAT_EVENT_TYPES: readonly EventType[] = ["created", "message", "question", "report", "comment", "progress"]

export type RunStatus = "CREATING" | "RUNNING" | "FINISHED" | "ERROR" | "CANCELLED" | "EXPIRED"
export const ACTIVE_RUN_STATUSES: readonly RunStatus[] = ["CREATING", "RUNNING"]

export interface AgentLink {
  id: string
  /** Страница агента в Cursor Web */
  url: string
  runId: string | null
  runStatus: RunStatus | null
  branch: string | null
  prUrl: string | null
  startedAt: string
  lastSyncAt: string | null
}

export interface TaskReport {
  summary: string
  /** Подробности в markdown: что сделано, как проверить */
  details: string | null
  /** Развёрнутая сборка именно этого фикса */
  previewUrl: string | null
  /** Ссылка для мобильного приложения (WebView) */
  mobileUrl: string | null
  branch: string | null
  mrUrl: string | null
  checks: string | null
  screenshots: Attachment[]
  at: string
  runId: string | null
}

/** Сообщение человека, ожидающее отправки агенту (агент занят текущим run) */
export interface PendingMessage {
  id: string
  at: string
  text: string
  author: Person
  attachments: Attachment[]
  /** Статус, в который перейдёт карточка при отправке */
  next: TaskStatus
}

export interface BugfixTask {
  id: string
  number: number
  title: string
  description: string
  status: TaskStatus
  /** Чем занят агент: первичная задача или доработка — сюда карточка вернётся после ответа на вопрос */
  phase: "initial" | "rework"
  author: Person
  workspaceId: number | null
  createdAt: string
  updatedAt: string
  attachments: Attachment[]
  agent: AgentLink | null
  report: TaskReport | null
  events: TaskEvent[]
  pending: PendingMessage[]
  /** Последний вопрос агента, пока карточка в «Требует реакции» */
  question: string | null
  /** Ошибка запуска или связи с Cursor */
  error: string | null
  /** Последнее промежуточное сообщение агента (прогресс) */
  progress: string | null
  /** Когда пользователь последний раз открывал карточку: userId → ISO */
  seenBy: Record<string, string>
  /** Отметка «сам остановил» — CANCELLED по нашей команде не считается ошибкой */
  stopRequested?: boolean
  /**
   * Колонка, из которой задачу отправили в архив.
   * Пока поле задано, карточка только в «Архиве»; возврат кладёт её обратно в эту колонку.
   */
  archivedFrom?: TaskStatus | null
  /**
   * Колонка, из которой задачу положили на паузу.
   * Пока статус paused, вернуть можно только в неё.
   */
  pausedFrom?: TaskStatus | null
}

/** Карточка канбана — без ленты событий */
export interface TaskCard {
  id: string
  number: number
  title: string
  status: TaskStatus
  phase: BugfixTask["phase"]
  author: Person
  createdAt: string
  updatedAt: string
  attachmentsCount: number
  agentUrl: string | null
  runStatus: RunStatus | null
  branch: string | null
  prUrl: string | null
  previewUrl: string | null
  mobileUrl: string | null
  question: string | null
  progress: string | null
  error: string | null
  pendingCount: number
  eventsCount: number
  /** Есть события новее последнего просмотра текущим пользователем */
  unread: boolean
  lastEvent: { at: string; type: EventType; text: string | null } | null
  /** Колонка до архивации; null — карточка на доске */
  archivedFrom: TaskStatus | null
  /** Колонка до паузы; null — задача не на паузе */
  pausedFrom: TaskStatus | null
}

export interface BugfixConfigStatus {
  enabled: boolean
  repoUrl: string
  baseBranch: string
  envName: string | null
  model: string | null
  autoCreatePr: boolean
  publicUrl: string | null
  deploy: "vercel" | "custom" | "none"
  devWebUrl: string
  missing: string[]
  warnings: string[]
  canManage: boolean
  me: Person
}

/** Переходы, которые человек может сделать вручную (остальные делает сервер по ответам агента) */
export const HUMAN_TRANSITIONS: Record<TaskStatus, readonly TaskStatus[]> = {
  request: ["paused"],
  in_progress: ["paused"],
  needs_attention: ["rework", "paused"],
  rework: ["paused"],
  review: ["rework", "awaiting_merge", "paused"],
  awaiting_merge: ["rework", "merged", "review", "paused"],
  merged: ["rework", "paused"],
  paused: [],
}

/** Переходы с обязательным комментарием (комментарий уходит агенту) */
export const COMMENT_REQUIRED: readonly TaskStatus[] = ["rework"]

export function canTransition(from: TaskStatus, to: TaskStatus): boolean {
  return HUMAN_TRANSITIONS[from].includes(to)
}

/** Можно ли бросить карточку в колонку. С паузы — только в колонку, откуда её взяли. */
export function canDrop(from: TaskStatus, to: TaskStatus, pausedFrom?: TaskStatus | null): boolean {
  if (from === to) return false
  if (from === "paused") return !!pausedFrom && to === pausedFrom
  return canTransition(from, to)
}

/** Колонка на доске: пока агент работает, карточка в «В работе». Пауза, доработка и вопрос человека остаются на месте. */
export function columnWhileWorking(status: TaskStatus, runStatus: RunStatus | null, archived: boolean): TaskStatus {
  if (archived || status === "paused" || status === "rework" || status === "needs_attention" || status === "in_progress") return status
  if (runStatus === "CREATING" || runStatus === "RUNNING") return "in_progress"
  return status
}

export function attachmentKind(mime: string, name: string): AttachmentKind {
  const m = mime.toLowerCase()
  if (m.startsWith("image/")) return "image"
  if (m.startsWith("video/")) return "video"
  if (m.startsWith("audio/")) return "audio"
  const ext = name.split(".").pop()?.toLowerCase() ?? ""
  if (["png", "jpg", "jpeg", "gif", "webp", "heic", "bmp", "svg"].includes(ext)) return "image"
  if (["mp4", "mov", "webm", "mkv", "avi", "m4v", "3gp"].includes(ext)) return "video"
  if (["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(ext)) return "audio"
  return "file"
}

const MIME_BY_EXT: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  bmp: "image/bmp",
  svg: "image/svg+xml",
  mp4: "video/mp4",
  m4v: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  mkv: "video/x-matroska",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  m4a: "audio/mp4",
  pdf: "application/pdf",
  txt: "text/plain",
  json: "application/json",
  zip: "application/zip",
}

export function mimeFromName(name: string): string {
  return MIME_BY_EXT[name.split(".").pop()?.toLowerCase() ?? ""] ?? "application/octet-stream"
}

/** MIME, присланный браузером: только «тип/подтип», иначе по расширению имени */
export function cleanMime(raw: unknown, name: string): string {
  const m = typeof raw === "string" ? raw.split(";")[0].trim().toLowerCase() : ""
  return /^[a-z0-9.+-]+\/[a-z0-9.+-]+$/.test(m) && m !== "application/octet-stream" ? m : mimeFromName(name)
}
